// Right rail — the three hand-off steps, plus a state-dependent footer action.
//
// `mode` mirrors whichever left panel is showing:
//   "devices"   — app open + signed in
//   "signedOut" — app open, nobody signed in (never offer an install here)
//   "wrongUser" — app open, signed in as somebody else
//   "install"   — app closed, or the user asked for the install flow
//
// `handoff` is owned by the page (see Connect/index.js) and shared with the
// left panel, so whichever button you press locks both. Any error it produces
// is rendered by the panel, which is on screen whenever these buttons are.
import styles from "../Connect.module.css";
import ArrowRightIcon from "../../../assets/icons/arrow-right-desktop.svg";
import { CheckIcon, DownloadIcon, ShieldIcon } from "./ConnectIcons";

// The three numbered steps, shown in every page state.
const HANDOFF_STEPS = [
  { n: "01", title: "Download the app", desc: "Choose the correct version for your device." },
  { n: "02", title: "Install and open", desc: "Your work session will be handed off securely." },
  { n: "03", title: "Tap Connect", desc: "Only the app can start the protected tunnel." },
];

const KICKER = {
  devices:   "SECURE HANDOFF",
  signedOut: "ALMOST THERE",
  wrongUser: "ACCOUNT MISMATCH",
  install:   "GET CONNECTED",
};
const TITLE = {
  devices:   "From browser to protected.",
  signedOut: "One step left — sign in.",
  wrongUser: "The app belongs to someone else.",
  install:   "Three steps to protection.",
};

export default function StepsRail({ mode, handoff, onInstallElsewhere, onRetry }) {
  const { signIn, pending } = handoff;

  // Steps 1 and 2 are satisfied the moment the app answers the health probe,
  // even if the account is wrong or missing — only step 3 needs a matching one.
  const doneThrough = mode === "devices" ? 3
    : mode === "signedOut" || mode === "wrongUser" ? 2
    : 0;

  return (
    <aside className={styles.rail}>
      <span className={styles.kicker}>{KICKER[mode]}</span>
      <h2 className={styles.railTitle}>{TITLE[mode]}</h2>

      <ol className={styles.steps}>
        {HANDOFF_STEPS.map((step, i) => (
          <li key={step.n} className={styles.step}>
            <span className={styles.stepNum}>{step.n}</span>
            <span className={styles.stepText}>
              <span className={styles.stepTitle}>{step.title}</span>
              <span className={styles.stepDesc}>{step.desc}</span>
            </span>
            {i < doneThrough && (
              <span className={styles.stepCheck}><CheckIcon size={16} /></span>
            )}
          </li>
        ))}
      </ol>

      <div className={styles.railFooter}>
        {mode === "devices" && (
          <button
            type="button"
            className={`${styles.btn} ${styles.btnGhost}`}
            onClick={onInstallElsewhere}
          >
            <DownloadIcon size={16} />
            INSTALL ON ANOTHER DEVICE
          </button>
        )}

        {mode === "signedOut" && (
          <>
            <div className={styles.hintCard}>
              <span className={styles.hintIcon}><ShieldIcon size={18} /></span>
              <span className={styles.hintText}>
                <span className={styles.hintTitle}>App running — not signed in</span>
                <span className={styles.hintDesc}>
                  Hand your dashboard session to the app — no password needed.
                  This page updates on its own once it takes effect.
                </span>
              </span>
            </div>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={signIn}
              disabled={pending}
            >
              {pending ? "SIGNING IN…" : "SIGN IN TO THE APP"}
              {!pending && <img src={ArrowRightIcon} alt="" className={styles.btnIcon} />}
            </button>
          </>
        )}

        {mode === "wrongUser" && (
          <>
            <div className={styles.hintCard}>
              <span className={styles.hintIcon}><ShieldIcon size={18} /></span>
              <span className={styles.hintText}>
                <span className={styles.hintTitle}>Accounts don't match</span>
                <span className={styles.hintDesc}>
                  Switch the app to the account you're using here. It signs the
                  other user out for you.
                </span>
              </span>
            </div>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={signIn}
              disabled={pending}
            >
              {pending ? "SWITCHING…" : "SWITCH TO MY ACCOUNT"}
              {!pending && <img src={ArrowRightIcon} alt="" className={styles.btnIcon} />}
            </button>
          </>
        )}

        {mode === "install" && (
          <>
            <div className={styles.hintCard}>
              <span className={styles.hintIcon}><ShieldIcon size={18} /></span>
              <span className={styles.hintText}>
                <span className={styles.hintTitle}>Already installed?</span>
                <span className={styles.hintDesc}>
                  Open the app, then try the secure handoff again.
                </span>
              </span>
            </div>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnGhost}`}
              onClick={onRetry}
            >
              I'VE INSTALLED IT · TRY AGAIN
            </button>
          </>
        )}
      </div>
    </aside>
  );
}
